import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { CategoryCard } from '../components/shared/CategoryCard';
import { ChevronRightIcon } from 'lucide-react';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { HomeIcon } from 'lucide-react';
export function CategoriesPage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const categories = [{
    id: 'medicines',
    name: 'Medicines & Treatments',
    icon: '💊',
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    productCount: 850,
    subcategories: ['Pain Relief', 'Cold & Flu', 'Allergies', 'Digestive Health', 'Sleep Aids'],
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop'
  }, {
    id: 'vitamins',
    name: 'Vitamins & Supplements',
    icon: '💪',
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    productCount: 650,
    subcategories: ['Multivitamins', 'Vitamin D', 'Omega-3', 'Probiotics', 'Protein Powder'],
    image: 'https://images.unsplash.com/photo-1550572017-4bfa2d5c8f1d?w=400&h=400&fit=crop'
  }, {
    id: 'medical-devices',
    name: 'Medical Devices',
    icon: '🩺',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    productCount: 320,
    subcategories: ['Blood Pressure Monitors', 'Glucose Meters', 'Thermometers', 'Nebulizers', 'Pulse Oximeters'],
    image: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=400&h=400&fit=crop'
  }, {
    id: 'beauty',
    name: 'Beauty & Skin Care',
    icon: '✨',
    color: 'from-pink-500 to-pink-600',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-600',
    productCount: 780,
    subcategories: ['Facial Care', 'Hair Care', 'Body Care', 'Sun Protection', 'Anti-Aging'],
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=400&fit=crop'
  }, {
    id: 'baby-care',
    name: 'Baby & Mother Care',
    icon: '👶',
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    productCount: 520,
    subcategories: ['Baby Formula', 'Diapers', 'Baby Food', 'Prenatal Vitamins', 'Nursing Supplies'],
    image: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=400&h=400&fit=crop'
  }, {
    id: 'personal-care',
    name: 'Personal Care',
    icon: '🧴',
    color: 'from-teal-500 to-teal-600',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    productCount: 430,
    subcategories: ['Oral Care', 'Bath & Shower', 'Deodorants', 'Shaving', 'Hand Care'],
    image: 'https://images.unsplash.com/photo-1556228852-80f59c1bb3cf?w=400&h=400&fit=crop'
  }, {
    id: 'health-monitoring',
    name: 'Health Monitoring',
    icon: '📊',
    color: 'from-indigo-500 to-indigo-600',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    productCount: 280,
    subcategories: ['Fitness Trackers', 'Smart Scales', 'Heart Rate Monitors', 'Sleep Trackers', 'Health Apps'],
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=400&fit=crop'
  }, {
    id: 'herbal',
    name: 'Herbal & Natural',
    icon: '🌿',
    color: 'from-lime-500 to-lime-600',
    bgColor: 'bg-lime-50',
    textColor: 'text-lime-600',
    productCount: 390,
    subcategories: ['Green Tea', 'Ginseng', 'Turmeric', 'Essential Oils', 'Herbal Teas'],
    image: 'https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=400&h=400&fit=crop'
  }, {
    id: 'first-aid',
    name: 'First Aid & Safety',
    icon: '🚑',
    color: 'from-orange-500 to-orange-600',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    productCount: 210,
    subcategories: ['First Aid Kits', 'Bandages', 'Antiseptics', 'Safety Equipment', 'Emergency Supplies'],
    image: 'https://images.unsplash.com/photo-1603398938378-e54eab446dde?w=400&h=400&fit=crop'
  }];
  return <HelmetProvider data-id="element-6506" data-mp="1oh99jre00x0v">
      <SEO title="All Categories" description="Explore all Innova Pharmacy categories — health, beauty, mom & baby and more. Shop online with fast delivery across Saudi Arabia, secure checkout, great prices." canonical="/en/categories/" ogImage="https://cdn.innovaksa.net/og/categories.jpg" hreflangAr="/categories/" hreflangEn="/en/categories/" hreflangDefault="/categories/" data-id="element-6507" data-mp="1ogztavira48d" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6508" data-mp="5hs5eiwayrap">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-6509" data-mp="agmuu3orze3s">
          <AnnouncementBar data-id="element-6510" data-mp="afbi3edcqw0j" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-6511" data-mp="aipkunpxh78i" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6512" data-mp="pry25dwkw43" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-6513" data-mp="3mvovqws9bb3q">
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6514" data-mp="3dy4v9q27xiu6">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-6515" data-mp="176m4y2kkqkoh" />
          </div>
          <h1 className="sr-only" data-id="element-6516" data-mp="2wosrbs6xzy4o">
            Shop by Category - Complete Healthcare Product Range
          </h1>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'All Categories'
        }]} title="All Categories" description="Explore all Innova Pharmacy categories — health, beauty, mom & baby and more. Shop online with fast delivery across Saudi Arabia." data-id="element-6517" data-mp="36du6xh9ksdi4" />
          {/* Categories Grid */}
          <div className="container mx-auto px-4 py-8" data-id="element-6518" data-mp="3palxmbrg5uw7">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6519" data-mp="3ken3s8ncnkoe">
              {categories.map(category => <CategoryCard key={category.id} category={category} data-id="element-6520" data-mp="1uyxh455khk9p" />)}
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-6521" data-mp="146ethzbua8li">
          <Footer data-id="element-6522" data-mp="145lw3oyrsvg1" />
        </div>
        <MobileFooterNav data-id="element-6523" data-mp="144ar5ltl9hnc" />
        <PharmacistChat data-id="element-6524" data-mp="19588jw6ry7ns" />
        <div className="h-16 md:hidden" data-id="element-6525" data-mp="194q7mm96359j"></div>
      </div>
    </HelmetProvider>;
}